import { useState, useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import PageHeader from "../../components/PageHeader";
import SectionLabel from "../../components/SectionLabel";
import API_URL from "../../config";
import { useAuth } from "../../context/AuthContext";

const SpectacleEdit = () => {
  const { token, role } = useAuth();
  const isAdmin = role === "ROLE_ADMIN";
  const [artists, setArtists] = useState([]);
  const [producteurs, setProducteurs] = useState([]);
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    date: "",
    location: "",
    price: "",
    artistId: "",
    coArtistIds: [],
    producerId: "",
    coProducerIds: [],
  });

  useEffect(() => {
    const calls = [
      fetch(`${API_URL}/api/spectacles/${id}`).then((res) => res.json()),
      fetch(`${API_URL}/api/artists`).then((res) => res.json()),
    ];
    if (isAdmin) {
      calls.push(
        fetch(`${API_URL}/api/users`, {
          headers: { Authorization: `Bearer ${token}` },
        }).then((res) => res.json()),
      );
    }
    Promise.all(calls)
      .then((results) => {
        const [spectacleData, artistsData, usersData] = results;
        setFormData({
          title: spectacleData.title || "",
          description: spectacleData.description || "",
          date: spectacleData.date ? spectacleData.date.substring(0, 16) : "",
          location: spectacleData.location || "",
          price: spectacleData.price || "",
          artistId: spectacleData.artist ? String(spectacleData.artist.id) : "",
          coArtistIds: Array.isArray(spectacleData.coArtists)
            ? spectacleData.coArtists.map((a) => String(a.id))
            : [],
          producerId: spectacleData.producer
            ? String(spectacleData.producer.id)
            : "",
          coProducerIds: Array.isArray(spectacleData.coProducers)
            ? spectacleData.coProducers.map((p) => String(p.id))
            : [],
        });
        setArtists(Array.isArray(artistsData) ? artistsData : []);
        if (isAdmin) {
          const list = Array.isArray(usersData) ? usersData : [];
          setProducteurs(list.filter((u) => u.role === "ROLE_PRODUCTEUR"));
        }
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });
  }, [id, isAdmin, token]);

  const handleChange = (e) =>
    setFormData({ ...formData, [e.target.id]: e.target.value });

  const handleMultiChange = (key) => (e) => {
    const values = Array.from(e.target.selectedOptions, (o) => o.value);
    setFormData({ ...formData, [key]: values });
  };

  const handleSubmit = () => {
    if (
      !formData.title ||
      !formData.date ||
      !formData.price ||
      !formData.artistId
    ) {
      setError("Titre, date, prix et artiste principal sont obligatoires.");
      return;
    }
    setSaving(true);
    setError(null);

    const primaryArtistId = parseInt(formData.artistId);
    const coArtists = formData.coArtistIds
      .map((cid) => parseInt(cid))
      .filter((cid) => !isNaN(cid) && cid !== primaryArtistId)
      .map((cid) => ({ id: cid }));

    const body = {
      title: formData.title,
      description: formData.description,
      date: new Date(formData.date).toISOString(),
      location: formData.location,
      price: parseFloat(formData.price),
      artist: { id: primaryArtistId },
      coArtists,
    };

    if (isAdmin) {
      const primaryProducerId = formData.producerId
        ? parseInt(formData.producerId)
        : null;
      body.producer = primaryProducerId ? { id: primaryProducerId } : null;
      body.coProducers = formData.coProducerIds
        .map((pid) => parseInt(pid))
        .filter((pid) => !isNaN(pid) && pid !== primaryProducerId)
        .map((pid) => ({ id: pid }));
    }

    fetch(`${API_URL}/api/spectacles/${id}`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(body),
    })
      .then((res) => {
        if (!res.ok)
          return res.text().then((t) => {
            throw new Error(t);
          });
        return res.json();
      })
      .then(() => navigate("/"))
      .catch((err) => {
        setError(err.message);
        setSaving(false);
      });
  };

  if (loading)
    return (
      <div className="text-center py-5 mt-5">
        <div className="spinner-border text-primary"></div>
      </div>
    );

  const coArtistOptions = artists.filter(
    (a) => String(a.id) !== String(formData.artistId),
  );
  const coProducerOptions = producteurs.filter(
    (p) => String(p.id) !== String(formData.producerId),
  );

  return (
    <>
      <PageHeader
        title="Modifier le spectacle"
        subtitle={`Modification de : ${formData.title}`}
        breadcrumb={[
          { label: "Spectacles", path: "/" },
          { label: `Modifier — ${formData.title}` },
        ]}
      />

      <section className="py-5 bg-light">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-8">
              {error && (
                <div className="alert-error">
                  <i className="fas fa-exclamation-circle me-2"></i>
                  {error}
                </div>
              )}

              <div className="agency-card">
                <div className="card-accent"></div>
                <div className="card-content">
                  <SectionLabel
                    icon="theater-masks"
                    text="Informations principales"
                  />

                  <div className="row g-3 mb-3">
                    <div className="col-md-6">
                      <label className="agency-label">Titre *</label>
                      <input
                        type="text"
                        id="title"
                        className="form-control"
                        value={formData.title}
                        onChange={handleChange}
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="agency-label">Lieu</label>
                      <input
                        type="text"
                        id="location"
                        className="form-control"
                        value={formData.location}
                        onChange={handleChange}
                      />
                    </div>
                  </div>

                  <div className="row g-3">
                    <div className="col-md-6">
                      <label className="agency-label">Date *</label>
                      <input
                        type="datetime-local"
                        id="date"
                        className="form-control"
                        value={formData.date}
                        onChange={handleChange}
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="agency-label">Prix (€) *</label>
                      <input
                        type="number"
                        id="price"
                        className="form-control"
                        min="0"
                        step="0.01"
                        value={formData.price}
                        onChange={handleChange}
                      />
                    </div>
                  </div>

                  <hr className="agency-divider" />
                  <SectionLabel icon="music" text="Artistes" />

                  <div className="row g-3">
                    <div className="col-md-6">
                      <label className="agency-label">Artiste principal *</label>
                      <select
                        id="artistId"
                        className="form-control"
                        value={formData.artistId}
                        onChange={handleChange}
                      >
                        <option value="">Sélectionner un artiste</option>
                        {artists.map((artist) => (
                          <option key={artist.id} value={artist.id}>
                            {artist.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-md-6">
                      <label className="agency-label">
                        Autres artistes (Ctrl/Cmd + clic)
                      </label>
                      <select
                        multiple
                        className="form-control"
                        value={formData.coArtistIds}
                        onChange={handleMultiChange("coArtistIds")}
                        size={Math.min(6, Math.max(3, coArtistOptions.length))}
                      >
                        {coArtistOptions.map((artist) => (
                          <option key={artist.id} value={artist.id}>
                            {artist.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {isAdmin && (
                    <>
                      <hr className="agency-divider" />
                      <SectionLabel icon="briefcase" text="Producteurs (admin)" />

                      <div className="row g-3">
                        <div className="col-md-6">
                          <label className="agency-label">
                            Producteur principal
                          </label>
                          <select
                            id="producerId"
                            className="form-control"
                            value={formData.producerId}
                            onChange={handleChange}
                          >
                            <option value="">Aucun</option>
                            {producteurs.map((p) => (
                              <option key={p.id} value={p.id}>
                                {p.firstName} {p.lastName} ({p.email})
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="col-md-6">
                          <label className="agency-label">
                            Autres producteurs (Ctrl/Cmd + clic)
                          </label>
                          <select
                            multiple
                            className="form-control"
                            value={formData.coProducerIds}
                            onChange={handleMultiChange("coProducerIds")}
                            size={Math.min(
                              6,
                              Math.max(3, coProducerOptions.length || 3),
                            )}
                          >
                            {coProducerOptions.map((p) => (
                              <option key={p.id} value={p.id}>
                                {p.firstName} {p.lastName} ({p.email})
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </>
                  )}

                  <hr className="agency-divider" />
                  <SectionLabel icon="file-alt" text="Description" />

                  <textarea
                    id="description"
                    className="form-control"
                    rows="5"
                    maxLength="1000"
                    value={formData.description}
                    onChange={handleChange}
                  />
                  <div className="char-count">
                    {formData.description.length} / 1000
                  </div>

                  <hr className="agency-divider" />
                  <div className="d-flex justify-content-between align-items-center">
                    <Link to="/" className="btn-cancel">
                      <i className="fas fa-arrow-left me-2"></i>Annuler
                    </Link>
                    <button
                      onClick={handleSubmit}
                      disabled={saving}
                      className="btn btn-primary btn-xl text-uppercase btn-admin"
                    >
                      {saving ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2"></span>
                          Sauvegarde...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-save me-2"></i>Sauvegarder
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default SpectacleEdit;
